import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, 
  Search, 
  Filter,
  Cpu,
  Wifi,
  WifiOff,
  Battery,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  Power,
  RotateCcw,
  Settings,
  ShieldAlert,
  Loader2,
  AlertCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { controllersService, companiesService, serversService, localSchedulesService, swatidService, alarmsService } from '@/services';
import type { ApiController, CreateControllerRequest, UpdateControllerRequest, ControllerOperationMode, CreateSwatidControllerRequest, UpdateSwatidControllerRequest } from '@/types/api';
import type { Controller } from '@/types';
import { isSwatidController, toSwatidMode } from '@/utils/controllerType';
import type { SwatidOperationMode } from '@/constants/swatid';
import { ControllerFormDialog } from '@/components/controllers/ControllerFormDialog';
import { SwatidControllerFormDialog } from '@/components/swatid/SwatidControllerFormDialog';
import { ControllerDetailDialog } from '@/components/controllers/ControllerDetailDialog';
import { OperationModeSelector } from '@/components/controllers/OperationModeSelector';
import { SwatidModeSelector } from '@/components/swatid/SwatidModeSelector';
import { ValveControlCompact } from '@/components/controllers/ValveControlPanel';
import { SchedulesVerificationGlobalPanel } from '@/components/schedules/SchedulesVerificationGlobalPanel';
import { VerificationBadge } from '@/components/schedules/VerificationBadge';
import { useControllersVerificationStatuses } from '@/hooks/useControllersVerificationStatuses';
import { useVerificationProgressToasts } from '@/hooks/useVerificationProgressToasts';
import { SCHEDULE_VERIFICATION_ALARM_TYPES } from '@/utils/scheduleVerification';
import {
  hasActiveVerificationSession,
  listActiveVerificationSessions,
} from '@/utils/verificationSession';
import { buildVerifyToast } from '@/hooks/useScheduleVerification';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

// Helpers para estados pendientes
const hasPendingModeChange = (controller: ApiController): boolean => {
  return controller.pendingOperationMode !== null && controller.pendingOperationMode !== undefined;
};

const getPendingDuration = (controller: ApiController): number => {
  if (!controller.pendingModeAt) return 0;
  return Date.now() - new Date(controller.pendingModeAt).getTime();
};

const isPendingTooLong = (controller: ApiController): boolean => {
  return getPendingDuration(controller) > 5 * 60 * 1000; // 5 minutos
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// Skeleton component
function ControllerCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div>
              <Skeleton className="h-5 w-32 mb-1" />
              <Skeleton className="h-3 w-16" />
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Skeleton className="h-5 w-16" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="pt-3 border-t">
          <Skeleton className="h-4 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

export default function Controllers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSwatidFormOpen, setIsSwatidFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedController, setSelectedController] = useState<ApiController | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { canManageDevices, currentUser } = useAuth();
  const queryClient = useQueryClient();
  const { lastValveAction, isConnected: wsConnected } = useWebSocket();
  const [verifyingControllerId, setVerifyingControllerId] = useState<string | null>(null);
  const [activeVerifySessions, setActiveVerifySessions] = useState(() =>
    listActiveVerificationSessions().map((s) => s.controllerId)
  );

  // Avisos de progreso LoRa aunque el diálogo de detalle esté cerrado
  useVerificationProgressToasts(true);

  useEffect(() => {
    const syncSessions = () => {
      setActiveVerifySessions(
        listActiveVerificationSessions().map((s) => s.controllerId)
      );
    };
    syncSessions();
    window.addEventListener('irrigo:verification-sessions-changed', syncSessions);
    window.addEventListener('irrigo:verification-progress', syncSessions);
    const id = window.setInterval(syncSessions, 30_000);
    return () => {
      window.removeEventListener('irrigo:verification-sessions-changed', syncSessions);
      window.removeEventListener('irrigo:verification-progress', syncSessions);
      window.clearInterval(id);
    };
  }, []);

  // State para acciones pendientes de válvulas (esperando confirmación del backend)
  // Key: `${controllerId}-${channel}`, Value: { action: 'open' | 'close', previousStatus: 'open' | 'closed' }
  const [pendingValveActions, setPendingValveActions] = useState<Map<string, { 
    action: 'open' | 'close'; 
    previousStatus: 'open' | 'closed';
  }>>(new Map());

  // Helper para generar key de válvula
  const getValveKey = (controllerId: string, channel: number) => `${controllerId}-${channel}`;

  // Comprobar si una válvula tiene acción pendiente
  const getValvePendingAction = (controllerId: string, channel: number) => {
    const key = getValveKey(controllerId, channel);
    return pendingValveActions.get(key)?.action || null;
  };

  // Query para obtener controladores con polling para actualizar estados de válvulas
  // CAMBIO v0.2.0: Filtrar por companyId si el usuario es company_admin
  const { 
    data: controllersData, 
    isLoading, 
    error,
    refetch 
  } = useQuery({
    queryKey: ['controllers', searchTerm, currentUser?.companyId],
    queryFn: async () => {
      try {
        const params: { pageSize: number; companyId?: string } = { pageSize: 100 };
        // Si el usuario es company_admin, filtrar por su empresa
        if (currentUser?.role === 'company_admin' && currentUser.companyId) {
          params.companyId = currentUser.companyId;
        }
        const response = await controllersService.getAll(params);
        if (!response.success) {
          console.error('[Controllers] Error response:', response);
          throw new Error(response.error?.message || 'Error al cargar controladores');
        }
        console.log('[Controllers] Loaded controllers:', response.data?.items?.length || 0, 'items');
        return response.data;
      } catch (err) {
        console.error('[Controllers] Error fetching controllers:', err);
        throw err;
      }
    },
    // Polling más frecuente (5s) cuando hay acciones pendientes, sino cada 30s
    refetchInterval: pendingValveActions.size > 0 ? 5000 : 30000,
    // Solo hacer polling cuando la ventana está activa
    refetchIntervalInBackground: false,
  });

  // CAMBIO v0.2.0: Controllers pertenecen a empresas, no a jardines
  // Query para obtener empresas (para el formulario de creación)
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await companiesService.getAll({ pageSize: 100 });
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al cargar empresas');
      }
      return response.data;
    },
    // Solo superadmin necesita la lista de empresas
    enabled: currentUser?.role === 'superadmin',
  });

  // Query para obtener servidores MQTT
  // ACTUALIZADO: Backend ahora devuelve formato paginado { items: [], pagination: {} }
  const { data: mqttServersData } = useQuery({
    queryKey: ['mqtt-servers'],
    queryFn: async () => {
      const response = await serversService.getMQTTServers();
      if (!response.success) {
        return [];
      }
      return response.data?.items || [];
    },
  });

  // Polling para controladoras con cambio pendiente
  useEffect(() => {
    if (!controllersData?.items) return;
    
    const controllersWithPending = controllersData.items.filter(hasPendingModeChange);
    if (controllersWithPending.length === 0) return;
    
    const interval = setInterval(() => {
      // Refetch solo las controladoras con cambio pendiente
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
    }, 10000); // cada 10 segundos
    
    return () => clearInterval(interval);
  }, [controllersData?.items, queryClient]);

  // Query para obtener servidores LNS
  // ACTUALIZADO: Backend ahora devuelve formato paginado { items: [], pagination: {} }
  const { data: lnsServersData } = useQuery({
    queryKey: ['lns-servers'],
    queryFn: async () => {
      const response = await serversService.getLNSServers();
      if (!response.success) {
        return [];
      }
      return response.data?.items || [];
    },
  });

  // Mutation para crear
  const createMutation = useMutation({
    mutationFn: (data: CreateControllerRequest) => controllersService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
        toast({ title: 'Controladora creada', description: 'La controladora ha sido creada correctamente.' });
        setIsFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para actualizar
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateControllerRequest }) => 
      controllersService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
        toast({ title: 'Controladora actualizada', description: 'La controladora ha sido actualizada correctamente.' });
        setIsFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation SWATID crear
  const createSwatidMutation = useMutation({
    mutationFn: (data: CreateSwatidControllerRequest) => swatidService.create(data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['controllers'] });
        toast({ title: 'SWATID creada', description: 'Controladora registrada correctamente.' });
        setIsSwatidFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const updateSwatidMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSwatidControllerRequest }) =>
      swatidService.update(id, data),
    onSuccess: (response) => {
      if (response.success) {
        queryClient.invalidateQueries({ queryKey: ['controllers'] });
        toast({ title: 'SWATID actualizada' });
        setIsSwatidFormOpen(false);
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  const swatidModeMutation = useMutation({
    mutationFn: ({ id, mode }: { id: string; mode: SwatidOperationMode }) =>
      swatidService.setMode(id, { mode }),
    onSuccess: async (_response, variables) => {
      await queryClient.invalidateQueries({ queryKey: ['controllers'] });
      await queryClient.invalidateQueries({ queryKey: ['swatid-status', variables.id] });
      await queryClient.invalidateQueries({ queryKey: ['swatid-info', variables.id] });
      toast({ title: 'Modo SWATID actualizado' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para eliminar
  const deleteMutation = useMutation({
    mutationFn: (id: string) => controllersService.delete(id),
    onSuccess: (response) => {
      if (response.success) {
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
        toast({ title: 'Controladora eliminada', description: 'La controladora ha sido eliminada.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para cambiar modo de operación
  // IMPORTANTE: Según la API, se deben enviar también localMode y supervisionMode
  const operationModeMutation = useMutation({
    mutationFn: ({ id, mode, currentLocalMode, currentSupervisionMode }: { 
      id: string; 
      mode: ControllerOperationMode;
      currentLocalMode?: boolean;
      currentSupervisionMode?: boolean;
    }) => {
      // Determinar localMode y supervisionMode según el modo
      const localMode = mode === 'local';
      const supervisionMode = localMode ? (currentSupervisionMode || false) : false;
      
      return controllersService.setOperationMode(id, mode, { localMode, supervisionMode });
    },
    onSuccess: (response, variables) => {
      if (response.success) {
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
        const modeLabels: Record<ControllerOperationMode, string> = {
          local: 'Local',
          remote: 'Remoto',
          manual: 'Manual'
        };
        toast({ 
          title: 'Modo cambiado', 
          description: `Modo de operación cambiado a ${modeLabels[variables.mode]}.` 
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para reiniciar controlador
  const rebootMutation = useMutation({
    mutationFn: (controllerId: string) => controllersService.reboot(controllerId),
    onSuccess: (response, controllerId) => {
      if (response.success) {
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
        toast({ 
          title: 'Reinicio enviado', 
          description: `Comando de reinicio enviado a ${response.data?.controllerName || 'controladora'}. El dispositivo se reiniciará en unos segundos.`,
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Handler para reiniciar
  const handleReboot = (controller: ApiController) => {
    if (confirm(`¿Estás seguro de reiniciar la controladora "${controller.name}"?\n\nEsto enviará el comando de reinicio al dispositivo y reseteará el contador de secuencia de comandos.`)) {
      rebootMutation.mutate(controller.id);
    }
  };

  // Mutation para sincronizar todas las programaciones (solo superadmin)
  const syncAllSchedulesMutation = useMutation({
    mutationFn: async () => {
      const response = await localSchedulesService.fetchAll();
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al sincronizar programaciones');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      queryClient.invalidateQueries({ queryKey: ['local-schedules'] });
      toast({
        title: 'Sincronización iniciada',
        description: `Comandos enviados. Éxito: ${data.successCount}, Errores: ${data.errorCount}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation para sincronizar hora de todas las controladoras (solo superadmin)
  const syncAllTimeMutation = useMutation({
    mutationFn: async () => {
      const response = await controllersService.syncTimeAll();
      if (!response.success) {
        throw new Error(response.error?.message || 'Error al sincronizar hora');
      }
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['controllers'] });
      toast({
        title: 'Sincronización iniciada',
        description: `Comandos enviados. Éxito: ${data.successCount}, Errores: ${data.errorCount}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Verificar si es superadmin
  const isSuperAdmin = currentUser?.role === 'superadmin';

  // Añadir acción pendiente
  const addPendingAction = (controllerId: string, channel: number, action: 'open' | 'close', previousStatus: 'open' | 'closed') => {
    const key = getValveKey(controllerId, channel);
    setPendingValveActions(prev => {
      const newMap = new Map(prev);
      newMap.set(key, { action, previousStatus });
      return newMap;
    });
  };

  // Limpiar acción pendiente cuando el estado cambia
  const clearPendingAction = (controllerId: string, channel: number) => {
    const key = getValveKey(controllerId, channel);
    setPendingValveActions(prev => {
      const newMap = new Map(prev);
      newMap.delete(key);
      return newMap;
    });
  };

  // Limpiar acciones pendientes SOLO cuando llega un evento WebSocket valve.action
  // Esto asegura que esperamos la confirmación REAL del dispositivo
  useEffect(() => {
    if (!lastValveAction) return;
    
    const { controllerId, valveChannel, action } = lastValveAction;
    const key = getValveKey(controllerId, valveChannel);
    const pending = pendingValveActions.get(key);
    
    if (pending) {
      // Verificar que la acción recibida coincide con la esperada
      const expectedAction = pending.action;
      if (action === expectedAction) {
        clearPendingAction(controllerId, valveChannel);
        // El toast ya lo muestra el WebSocketContext, no duplicar aquí
        console.log(`[Controllers] WebSocket confirmó: válvula ${valveChannel} ${action}`);
      }
    }
  }, [lastValveAction]);
  
  // Fallback: Si no hay WebSocket, usar polling para detectar cambios
  // Esto se activa después de 30 segundos sin confirmación WebSocket
  useEffect(() => {
    if (wsConnected) return; // Si WebSocket está conectado, no usar fallback
    if (pendingValveActions.size === 0) return;
    if (!controllersData?.items) return;

    controllersData.items.forEach(controller => {
      controller.valves?.forEach(valve => {
        const key = getValveKey(controller.id, valve.channel);
        const pending = pendingValveActions.get(key);
        
        if (pending) {
          const expectedStatus = pending.action === 'open' ? 'open' : 'closed';
          if (valve.status === expectedStatus) {
            clearPendingAction(controller.id, valve.channel);
            toast({
              title: pending.action === 'open' ? 'Válvula abierta' : 'Válvula cerrada',
              description: `${valve.name} confirmada (polling).`,
            });
          }
        }
      });
    });
  }, [controllersData, wsConnected]);

  // Mutation para abrir válvula - SIN actualización optimista
  // El estado real de las válvulas viene SIEMPRE del backend
  const openValveMutation = useMutation({
    mutationFn: ({ controllerId, channel, duration, currentStatus }: { 
      controllerId: string; 
      channel: number; 
      duration?: number;
      currentStatus: 'open' | 'closed';
    }) => 
      controllersService.openValve(controllerId, channel, duration),
    onSuccess: (response, variables) => {
      if (response.success) {
        const durationMsg = variables.duration ? ` por ${variables.duration} minutos` : ' (permanente)';
        // Marcar como pendiente
        addPendingAction(variables.controllerId, variables.channel, 'open', variables.currentStatus);
        toast({ 
          title: 'Comando enviado', 
          description: `Encendiendo ${response.data?.valveName || 'válvula'}${durationMsg}. Esperando confirmación...` 
        });
        // Refetch más frecuente mientras hay acciones pendientes usando requestAnimationFrame
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // Mutation para cerrar válvula - SIN actualización optimista
  const closeValveMutation = useMutation({
    mutationFn: ({ controllerId, channel, currentStatus }: { 
      controllerId: string; 
      channel: number;
      currentStatus: 'open' | 'closed';
    }) => 
      controllersService.closeValve(controllerId, channel),
    onSuccess: (response, variables) => {
      if (response.success) {
        // Marcar como pendiente
        addPendingAction(variables.controllerId, variables.channel, 'close', variables.currentStatus);
        toast({ 
          title: 'Comando enviado', 
          description: `Apagando ${response.data?.valveName || 'válvula'}. Esperando confirmación...` 
        });
        // Refetch para obtener el estado real del backend usando requestAnimationFrame
        requestAnimationFrame(() => {
          queryClient.invalidateQueries({ queryKey: ['controllers'] });
        });
      } else {
        toast({ title: 'Error', description: response.error?.message, variant: 'destructive' });
      }
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    },
  });

  // State para tarjetas expandidas (mostrar controles de válvulas)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const toggleCardExpanded = (controllerId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(controllerId)) {
        newSet.delete(controllerId);
      } else {
        newSet.add(controllerId);
      }
      return newSet;
    });
  };

  // Handler para cambiar modo de operación
  const handleModeChange = (controllerId: string, mode: ControllerOperationMode) => {
    // Buscar el controlador actual para obtener sus valores de localMode y supervisionMode
    const controller = controllers.find(c => c.id === controllerId);
    operationModeMutation.mutate({ 
      id: controllerId, 
      mode,
      currentLocalMode: controller?.localMode,
      currentSupervisionMode: controller?.supervisionMode,
    });
  };

  // Handlers para válvulas
  const handleValveOpen = async (controllerId: string, channel: number, currentStatus: 'open' | 'closed', duration?: number) => {
    await openValveMutation.mutateAsync({ controllerId, channel, duration, currentStatus });
  };

  const handleValveClose = async (controllerId: string, channel: number, currentStatus: 'open' | 'closed') => {
    await closeValveMutation.mutateAsync({ controllerId, channel, currentStatus });
  };

  const controllers = controllersData?.items || [];
  const companies = companiesData?.items || [];

  // Filter controllers by search term
  const filteredControllers = controllers.filter((controller) => {
    const q = searchTerm.toLowerCase();
    return (
      controller.name.toLowerCase().includes(q) ||
      controller.deviceEUI?.toLowerCase().includes(q) ||
      controller.serialNumber?.toLowerCase().includes(q)
    );
  });

  const controllerIds = filteredControllers.map((c) => c.id);
  const { byId: verificationById, verifyController } =
    useControllersVerificationStatuses(controllerIds, controllerIds.length > 0);

  const { data: verificationAlarms = [] } = useQuery({
    queryKey: ['alarms-schedule-verification'],
    queryFn: async () => {
      const response = await alarmsService.getAll({
        status: 'active',
        pageSize: 50,
      });
      if (!response.success) return [];
      const items = response.data?.items ?? [];
      return items.filter((a) =>
        (SCHEDULE_VERIFICATION_ALARM_TYPES as readonly string[]).includes(a.type)
      );
    },
    staleTime: 60_000,
    refetchInterval: 120_000,
  });

  const handleVerifySchedules = async (controller: ApiController) => {
    setVerifyingControllerId(controller.id);
    try {
      const data = await verifyController(controller.id, true);
      const msg = buildVerifyToast(data, !isSwatidController(controller));
      toast({
        title: msg.title,
        description: `${controller.name}: ${msg.description}`,
        variant: msg.variant,
      });
      setActiveVerifySessions(
        listActiveVerificationSessions().map((s) => s.controllerId)
      );
    } catch (e) {
      toast({
        title: 'Error al verificar',
        description: e instanceof Error ? e.message : 'Error desconocido',
        variant: 'destructive',
      });
    } finally {
      setVerifyingControllerId(null);
    }
  };

  const handleCreate = () => {
    setSelectedController(null);
    setIsFormOpen(true);
  };

  const handleCreateSwatid = () => {
    setSelectedController(null);
    setIsSwatidFormOpen(true);
  };

  const handleEdit = (controller: ApiController) => {
    setSelectedController(controller);
    if (isSwatidController(controller)) {
      setIsSwatidFormOpen(true);
    } else {
      setIsFormOpen(true);
    }
  };

  const handleViewDetail = (controller: ApiController) => {
    setSelectedController(controller);
    setIsDetailOpen(true);
  };

  const handleSwatidFormSubmit = (data: {
    name: string;
    serialNumber: string;
    companyId?: string;
    lat?: number;
    lng?: number;
  }) => {
    const payload: CreateSwatidControllerRequest = {
      name: data.name,
      serialNumber: data.serialNumber.toUpperCase(),
      companyId: data.companyId || undefined,
      lat: data.lat,
      lng: data.lng,
    };
    if (selectedController) {
      const update: UpdateSwatidControllerRequest = {
        name: data.name,
        lat: data.lat,
        lng: data.lng,
      };
      if (currentUser?.role === 'superadmin') {
        update.serialNumber = data.serialNumber.toUpperCase();
        update.companyId = data.companyId || undefined;
      }
      updateSwatidMutation.mutate({ id: selectedController.id, data: update });
    } else {
      createSwatidMutation.mutate(payload);
    }
  };

  const handleFormSubmit = (data: CreateControllerRequest | UpdateControllerRequest) => {
    if (selectedController) {
      updateMutation.mutate({ id: selectedController.id, data: data as UpdateControllerRequest });
    } else {
      createMutation.mutate(data as CreateControllerRequest);
    }
  };

  const handleDelete = (controller: ApiController) => {
    if (confirm(`¿Estás seguro de eliminar la controladora "${controller.name}"?`)) {
      deleteMutation.mutate(controller.id);
    }
  };

  // CAMBIO v0.2.0: Controllers pertenecen a empresas
  const getCompanyName = (companyId: string | undefined | null) => {
    if (!companyId) return 'Sin asignar';
    return companies.find(c => c.id === companyId)?.name || 'Sin asignar';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'bg-success';
      case 'offline': return 'bg-destructive';
      case 'warning': return 'bg-warning';
      default: return 'bg-muted';
    }
  };

  const getBatteryColor = (battery?: number) => {
    if (!battery) return 'text-muted-foreground';
    if (battery > 50) return 'text-success';
    if (battery > 20) return 'text-warning';
    return 'text-destructive';
  };

  const formatLastSeen = (dateStr?: string) => {
    if (!dateStr) return 'Nunca';
    const date = new Date(dateStr);
    const diff = Date.now() - date.getTime();
    if (diff < 60000) return 'Ahora';
    if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)} h`;
    return `Hace ${Math.floor(diff / 86400000)} días`;
  };

  // Stats
  const onlineCount = controllers.filter(c => c.status === 'online').length;
  const offlineCount = controllers.filter(c => c.status === 'offline').length;

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Controladoras</h1>
            <p className="text-muted-foreground mt-1">Gestiona los dispositivos UC511 y UC512</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => <ControllerCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-foreground">Controladoras</h1>
            <p className="text-muted-foreground mt-1">Gestiona los dispositivos UC511 y UC512</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>{error instanceof Error ? error.message : 'Error al cargar controladores'}</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Reintentar
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground">
            Controladoras
          </h1>
          <p className="text-muted-foreground mt-1">
            Milesight UC51x y controladoras SWATID-16
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <>
              <Button
                variant="outline"
                onClick={() => syncAllSchedulesMutation.mutate()}
                disabled={syncAllSchedulesMutation.isPending}
              >
                {syncAllSchedulesMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Sincronizar Programaciones
              </Button>
              <Button
                variant="outline"
                onClick={() => syncAllTimeMutation.mutate()}
                disabled={syncAllTimeMutation.isPending}
              >
                {syncAllTimeMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Clock className="h-4 w-4 mr-2" />
                )}
                Sincronizar Hora
              </Button>
            </>
          )}
          {canManageDevices ? (
            <>
              <Button
                variant="outline"
                onClick={handleCreateSwatid}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Nueva SWATID
              </Button>
              <Button onClick={handleCreate} className="gradient-primary text-white shadow-md hover:shadow-lg transition-shadow">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Milesight
              </Button>
            </>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button disabled className="gap-2">
                  <ShieldAlert className="w-4 h-4" />
                  Nueva Controladora
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Solo los superadmin pueden crear controladoras</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nombre, DevEUI o serie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" className="gap-2">
          <Filter className="w-4 h-4" />
          Filtros
        </Button>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Wifi className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{onlineCount}</p>
              <p className="text-sm text-muted-foreground">Online</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <WifiOff className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{offlineCount}</p>
              <p className="text-sm text-muted-foreground">Offline</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Cpu className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-display font-bold">{controllers.length}</p>
              <p className="text-sm text-muted-foreground">Total</p>
            </div>
          </div>
        </Card>
      </div>

      <SchedulesVerificationGlobalPanel />

      {verificationAlarms.length > 0 && (
        <Alert className="border-amber-500/40 bg-amber-500/5">
          <AlertCircle className="h-4 w-4 text-amber-700" />
          <AlertDescription className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {verificationAlarms.length} alarma(s) de sincronización de programaciones activas.
            </span>
            <Button
              variant="link"
              className="h-auto p-0"
              onClick={() => navigate('/alarms')}
            >
              Ver alarmas
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Controllers grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredControllers.map((controller) => {
          const swatid = isSwatidController(controller);
          return (
          <motion.div key={controller.id} variants={itemVariants}>
            <Card className="group hover:shadow-card-hover transition-all duration-300 hover:-translate-y-0.5">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      'p-2.5 rounded-xl',
                      controller.status === 'online' ? 'bg-success/10' : 'bg-muted'
                    )}>
                      <Cpu className={cn(
                        'w-5 h-5',
                        controller.status === 'online' ? 'text-success' : 'text-muted-foreground'
                      )} />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-foreground group-hover:text-primary transition-colors">
                        {controller.name}
                      </h3>
                      <p className="text-xs text-muted-foreground font-mono">
                        {swatid ? (controller.serialNumber || 'SWATID') : controller.model}
                      </p>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-popover">
                      <DropdownMenuItem onClick={() => handleViewDetail(controller)}>
                        <Eye className="w-4 h-4 mr-2" />
                        Ver detalles
                      </DropdownMenuItem>
                      {canManageDevices && (
                        <>
                          <DropdownMenuItem onClick={() => handleEdit(controller)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewDetail(controller)}>
                            <Settings className="w-4 h-4 mr-2" />
                            Configuración
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => void handleVerifySchedules(controller)}
                            disabled={verifyingControllerId === controller.id}
                          >
                            {verifyingControllerId === controller.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4 mr-2" />
                            )}
                            Verificar programaciones
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {!swatid && (
                          <DropdownMenuItem 
                            onClick={() => handleReboot(controller)}
                            disabled={rebootMutation.isPending}
                          >
                            {rebootMutation.isPending && rebootMutation.variables === controller.id ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4 mr-2" />
                            )}
                            Reiniciar dispositivo
                          </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            className="text-destructive focus:text-destructive"
                            onClick={() => handleDelete(controller)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status badges and Operation Mode */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge 
                      variant="outline" 
                      className={cn(
                        'gap-1.5',
                        controller.status === 'online' && 'border-success/50 text-success',
                        controller.status === 'offline' && 'border-destructive/50 text-destructive'
                      )}
                    >
                      <div className={cn('w-1.5 h-1.5 rounded-full', getStatusColor(controller.status))} />
                      {controller.status}
                    </Badge>
                    <Badge variant="secondary">
                      {swatid ? 'MQTT SWATID' : controller.communicationType.toUpperCase()}
                    </Badge>
                    {swatid && (
                      <Badge variant="outline" className="text-xs">16 relés</Badge>
                    )}
                    {(() => {
                      const inQueue =
                        activeVerifySessions.includes(controller.id) ||
                        hasActiveVerificationSession(controller.id);
                      if (inQueue) {
                        return (
                          <Badge
                            variant="outline"
                            className="text-xs bg-blue-500/10 text-blue-700 border-blue-500/30 gap-1"
                          >
                            <RefreshCw className="h-3 w-3 animate-spin" />
                            Verificando…
                          </Badge>
                        );
                      }
                      const snap = verificationById.get(controller.id);
                      if (!snap || snap.isLoading) return null;
                      if (snap.unavailable) {
                        return (
                          <Badge variant="outline" className="text-xs text-muted-foreground">
                            Verif. N/D
                          </Badge>
                        );
                      }
                      return <VerificationBadge status={snap.worstStatus} />;
                    })()}
                  </div>
                  {swatid ? (
                    <SwatidModeSelector
                      currentMode={toSwatidMode(controller.operationMode)}
                      onModeChange={(mode) =>
                        swatidModeMutation.mutate({ id: controller.id, mode })
                      }
                      isLoading={
                        swatidModeMutation.isPending &&
                        swatidModeMutation.variables?.id === controller.id
                      }
                      disabled={controller.status !== 'online'}
                      size="sm"
                    />
                  ) : (
                  <OperationModeSelector
                    currentMode={controller.operationMode || 'remote'}
                    onModeChange={(mode) => handleModeChange(controller.id, mode)}
                    isLoading={operationModeMutation.isPending && operationModeMutation.variables?.id === controller.id}
                    disabled={hasPendingModeChange(controller)}
                    size="sm"
                  />
                  )}
                </div>

                {!swatid && hasPendingModeChange(controller) && (
                  <div className="flex items-center gap-2 p-2 rounded-md bg-amber-500/10 border border-amber-500/20">
                    <Clock className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-amber-700 font-medium">
                        Cambiando a {controller.pendingOperationMode}...
                        {isPendingTooLong(controller) && (
                          <span className="ml-1 text-amber-800">(tardando más de lo esperado)</span>
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* Device info */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{swatid ? 'Serie' : 'DevEUI'}</span>
                    <span className="font-mono text-xs">
                      {swatid ? controller.serialNumber : controller.deviceEUI}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Empresa</span>
                    <span>{getCompanyName(controller.companyId)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Última conexión</span>
                    <span>{formatLastSeen(controller.lastSeen)}</span>
                  </div>
                </div>

                {/* Battery and valves / relays summary */}
                <div className="pt-3 border-t border-border flex items-center justify-between">
                  {!swatid && (
                  <div className="flex items-center gap-1.5">
                    <Battery className={cn('w-4 h-4', getBatteryColor(controller.battery))} />
                    <span className={cn('text-sm font-medium', getBatteryColor(controller.battery))}>
                      {controller.battery ?? 0}%
                    </span>
                  </div>
                  )}
                  {swatid ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs text-muted-foreground w-full justify-center"
                      onClick={() => handleViewDetail(controller)}
                    >
                      {controller.relayCount ?? 16} relés · Ver panel SWATID
                    </Button>
                  ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={() => toggleCardExpanded(controller.id)}
                  >
                    {controller.valves?.length || 0} válvulas · 
                    {controller.valves?.filter(v => v.status === 'open').length || 0} abiertas
                    {expandedCards.has(controller.id) ? (
                      <ChevronUp className="w-3.5 h-3.5 ml-1" />
                    ) : (
                      <ChevronDown className="w-3.5 h-3.5 ml-1" />
                    )}
                  </Button>
                  )}
                </div>

                {/* Expanded valve controls — solo Milesight */}
                {!swatid && expandedCards.has(controller.id) && controller.valves && controller.valves.length > 0 && (
                  <div className="pt-3 border-t border-border space-y-2">
                    <p className="text-xs font-medium text-muted-foreground mb-2">
                      Control de válvulas {controller.operationMode === 'manual' && '(Modo Manual Activo)'}
                    </p>
                    {controller.valves.map((valve) => (
                      <ValveControlCompact
                        key={valve.id}
                        valve={valve}
                        isManualMode={controller.operationMode === 'manual'}
                        onOpen={async (duration) => {
                          await handleValveOpen(controller.id, valve.channel, valve.status, duration);
                        }}
                        onClose={async () => {
                          await handleValveClose(controller.id, valve.channel, valve.status);
                        }}
                        isLoading={
                          (openValveMutation.isPending && openValveMutation.variables?.controllerId === controller.id && openValveMutation.variables?.channel === valve.channel) ||
                          (closeValveMutation.isPending && closeValveMutation.variables?.controllerId === controller.id && closeValveMutation.variables?.channel === valve.channel)
                        }
                        pendingAction={getValvePendingAction(controller.id, valve.channel)}
                      />
                    ))}
                    {controller.operationMode !== 'manual' && (
                      <p className="text-xs text-muted-foreground italic">
                        Activa el modo Manual para controlar las válvulas manualmente.
                      </p>
                    )}
                    
                    {/* Botón de reinicio en modo manual */}
                    {controller.operationMode === 'manual' && canManageDevices && (
                      <div className="pt-2 mt-2 border-t border-border">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full gap-2 text-xs"
                          onClick={() => handleReboot(controller)}
                          disabled={rebootMutation.isPending && rebootMutation.variables === controller.id}
                        >
                          {rebootMutation.isPending && rebootMutation.variables === controller.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                          )}
                          Reiniciar dispositivo
                        </Button>
                        <p className="text-[10px] text-muted-foreground mt-1 text-center">
                          Usa si el dispositivo no responde a comandos
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
          );
        })}
      </div>

      {filteredControllers.length === 0 && !isLoading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <Cpu className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-2">
            {controllers.length === 0 
              ? 'No se encontraron controladoras.' 
              : `No se encontraron controladoras que coincidan con "${searchTerm}".`}
          </p>
          {currentUser?.role === 'company_admin' && (
            <p className="text-xs text-muted-foreground">
              Mostrando solo controladoras de tu empresa.
            </p>
          )}
          {canManageDevices && (
            <Button className="mt-4" variant="outline" onClick={handleCreate}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primera controladora
            </Button>
          )}
        </motion.div>
      )}

      {/* Dialogs */}
      <ControllerFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        controller={selectedController && !isSwatidController(selectedController) ? {
          id: selectedController.id,
          name: selectedController.name,
          model: selectedController.model as 'UC511_V2' | 'UC511_V4' | 'UC512_V2' | 'UC512_V4',
          deviceEUI: selectedController.deviceEUI,
          companyId: selectedController.companyId,
          coordinates: selectedController.coordinates 
            ? { lat: selectedController.coordinates.lat, lng: selectedController.coordinates.lng }
            : undefined,
          communicationType: selectedController.communicationType,
          mqttConfig: selectedController.mqttServerId ? {
            serverId: selectedController.mqttServerId,
            subscriptionTopic: selectedController.mqttSubscriptionTopic || '',
            publishTopic: selectedController.mqttPublishTopic || '',
          } : undefined,
          lnsConfig: selectedController.lnsServerId ? {
            serverId: selectedController.lnsServerId,
          } : undefined,
          appKey: selectedController.appKey || undefined,
          valves: selectedController.valves?.map(v => ({
            id: v.id,
            controllerId: selectedController.id,
            channel: v.channel,
            name: v.name,
            status: v.status,
          })) || [],
          status: selectedController.status,
          lastSeen: selectedController.lastSeen ? new Date(selectedController.lastSeen) : undefined,
          battery: selectedController.battery,
          createdAt: new Date(selectedController.createdAt),
          updatedAt: new Date(selectedController.updatedAt),
        } : null}
        companies={companies.map(c => ({
          id: c.id,
          name: c.name,
        }))}
        mqttServers={mqttServersData || []}
        lnsServers={lnsServersData || []}
        onSubmit={handleFormSubmit}
        isLoading={createMutation.isPending || updateMutation.isPending}
        userRole={currentUser?.role}
      />
      <SwatidControllerFormDialog
        open={isSwatidFormOpen}
        onOpenChange={setIsSwatidFormOpen}
        controller={selectedController && isSwatidController(selectedController) ? selectedController : null}
        companies={companies.map((c) => ({ id: c.id, name: c.name }))}
        onSubmit={handleSwatidFormSubmit}
        isLoading={createSwatidMutation.isPending || updateSwatidMutation.isPending}
        userRole={currentUser?.role}
      />
      <ControllerDetailDialog
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        apiController={selectedController}
        controller={selectedController ? {
          id: selectedController.id,
          name: selectedController.name,
          model: selectedController.model as Controller['model'],
          deviceEUI: selectedController.deviceEUI,
          serialNumber: selectedController.serialNumber,
          relayCount: selectedController.relayCount,
          companyId: selectedController.companyId,
          coordinates: selectedController.coordinates 
            ? { lat: selectedController.coordinates.lat, lng: selectedController.coordinates.lng }
            : undefined,
          communicationType: selectedController.communicationType,
          mqttConfig: selectedController.mqttServerId ? {
            serverId: selectedController.mqttServerId,
            subscriptionTopic: selectedController.mqttSubscriptionTopic || '',
            publishTopic: selectedController.mqttPublishTopic || '',
          } : undefined,
          lnsConfig: selectedController.lnsServerId ? {
            serverId: selectedController.lnsServerId,
          } : undefined,
          operationMode: selectedController.operationMode,
          valves: selectedController.valves?.map(v => ({
            id: v.id,
            controllerId: selectedController.id,
            channel: v.channel,
            name: v.name,
            status: v.status,
          })) || [],
          status: selectedController.status,
          lastSeen: selectedController.lastSeen ? new Date(selectedController.lastSeen) : undefined,
          battery: selectedController.battery,
          createdAt: new Date(selectedController.createdAt),
          updatedAt: new Date(selectedController.updatedAt),
          // Campos v0.2 - Modo Local
          localMode: selectedController.localMode,
          supervisionMode: selectedController.supervisionMode,
          protocolVersion: selectedController.protocolVersion,
          timezoneOffset: selectedController.timezoneOffset,
          lastTimeSync: selectedController.lastTimeSync ? new Date(selectedController.lastTimeSync) : undefined,
          schedulesCount: selectedController.schedulesCount,
          enabledSchedulesCount: selectedController.enabledSchedulesCount,
        } : null}
        companyName={selectedController ? getCompanyName(selectedController.companyId) : undefined}
      />
    </motion.div>
  );
}
